import { useState } from "react";
import Swal from "sweetalert2";
import api from "../services/api";
import quoteService from "../services/quoteService";

export const useQuoteForm = (onSuccess) => {
  const [clienteNombre, setClienteNombre] = useState("");
  const [clienteTelefono, setClienteTelefono] = useState("");
  const [clienteDireccion, setClienteDireccion] = useState("");
  const [clienteEmail, setClienteEmail] = useState("");
  const [fechaVigencia, setFechaVigencia] = useState("");
  const [notas, setNotas] = useState("");
  const [descuento, setDescuento] = useState(0);

  const [items, setItems] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [saving, setSaving] = useState(false);

  /**
   * Busca productos usando el endpoint paginado, que es el único
   * que soporta el parámetro `search` en el backend actual.
   */
  const buscarProductos = async (term) => {
    setSearchTerm(term);

    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await api.get(
        `/products/paginated?search=${encodeURIComponent(term)}`
      );
      const data = response.data?.data ?? [];
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error al buscar productos:", error);
    } finally {
      setSearching(false);
    }
  };

  const agregarProducto = (producto) => {
    setItems((prev) => {
      const yaExiste = prev.find((i) => i.product_id === producto.id);
      if (yaExiste) {
        return prev.map((i) =>
          i.product_id === producto.id ? { ...i, cantidad: i.cantidad + 1 } : i
        );
      }

      return [
        ...prev,
        {
          product_id: producto.id,
          nombre_producto: producto.nombre,
          cantidad: 1,
          precio_unitario: producto.precio_venta,
        },
      ];
    });

    setSearchTerm("");
    setSearchResults([]);
  };

  const agregarProductoManual = (nombre) => {
    if (!nombre.trim()) return;

    setItems((prev) => [
      ...prev,
      {
        product_id: null,
        nombre_producto: nombre,
        cantidad: 1,
        precio_unitario: 0,
      },
    ]);
    setSearchTerm("");
    setSearchResults([]);
  };

  const actualizarItem = (index, campo, valor) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [campo]: valor } : item))
    );
  };

  const eliminarItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const subtotal = items.reduce(
    (acc, item) => acc + Number(item.cantidad) * Number(item.precio_unitario),
    0
  );
  const total = subtotal - Number(descuento || 0);

  const resetForm = () => {
    setClienteNombre("");
    setClienteTelefono("");
    setClienteDireccion("");
    setClienteEmail("");
    setFechaVigencia("");
    setNotas("");
    setDescuento(0);
    setItems([]);
    setSearchTerm("");
    setSearchResults([]);
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      Swal.fire("Atención", "Agrega al menos un producto a la cotización", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        cliente_nombre: clienteNombre || null,
        cliente_telefono: clienteTelefono || null,
        cliente_direccion: clienteDireccion || null,
        cliente_email: clienteEmail || null,
        fecha_vigencia: fechaVigencia || null,
        notas: notas || null,
        descuento: Number(descuento) || 0,
        items: items.map((i) => ({
          product_id: i.product_id,
          nombre_producto: i.nombre_producto,
          cantidad: Number(i.cantidad),
          precio_unitario: Number(i.precio_unitario),
        })),
      };

      const quote = await quoteService.createQuote(payload);

      Swal.fire("Listo", "Cotización generada correctamente", "success");
      resetForm();

      if (onSuccess) onSuccess(quote);
    } catch (error) {
      console.error("Error al generar cotización:", error);
      const mensaje =
        error?.response?.data?.error || "No se pudo generar la cotización";
      Swal.fire("Error", mensaje, "error");
    } finally {
      setSaving(false);
    }
  };

  return {
    clienteNombre, setClienteNombre,
    clienteTelefono, setClienteTelefono,
    clienteDireccion, setClienteDireccion,
    clienteEmail, setClienteEmail,
    fechaVigencia, setFechaVigencia,
    notas, setNotas,
    descuento, setDescuento,
    items,
    searchTerm,
    searchResults,
    searching,
    saving,
    subtotal,
    total,
    buscarProductos,
    agregarProducto,
    agregarProductoManual,
    actualizarItem,
    eliminarItem,
    handleSubmit,
    resetForm,
  };
};